/* Public per-studio site — Lumetier Phase 3.
   Lives at `/s/:slug`. Renders a self-contained single-page booking experience for any
   tenant studio: hero → about → services & booking → contact. Every API call goes through
   the tenant-scoped `/api/s/{slug}/...` endpoints so the data is isolated by URL.

   The page degrades gracefully if the studio hasn't customized their site_settings yet —
   the SiteSettings defaults will render until the owner edits them. Allure stays on its
   existing bare-path public site; only OTHER tenants live here. */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Calendar as CalendarIcon, Clock, Check, ArrowRight, Loader2,
  Mail, Phone, MapPin, ChevronRight, ChevronLeft, AlertTriangle, X,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { applyTheme } from "@/lib/themeApplier";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Eight default time slots — overridable from site_settings later (Phase 3.1)
const DEFAULT_SLOTS = ["09:00", "10:30", "12:00", "13:30", "15:00", "16:30", "18:00", "19:30"];

function fmtDate(d) {
  if (!d) return "";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

export default function StudioPublic() {
  const { slug } = useParams();
  const [studio, setStudio] = useState(null);
  const [site, setSite] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState({ blocked_dates: [], blocked_slots: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // {code, message}

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [infoR, siteR, svcR, availR] = await Promise.all([
          fetch(`${API_BASE}/api/s/${slug}/info`),
          fetch(`${API_BASE}/api/s/${slug}/site`),
          fetch(`${API_BASE}/api/s/${slug}/services`),
          fetch(`${API_BASE}/api/s/${slug}/availability`),
        ]);
        if (!infoR.ok) {
          const data = await infoR.json().catch(() => ({}));
          if (!cancelled) setError({ code: infoR.status, message: data.detail || "This studio isn't available." });
          return;
        }
        const [info, siteData, svc, avail] = await Promise.all([
          infoR.json(), siteR.json(), svcR.json(), availR.json(),
        ]);
        if (!cancelled) {
          setStudio(info);
          setSite(siteData);
          setServices(svc);
          setAvailability(avail);
        }
      } catch (e) {
        if (!cancelled) setError({ code: 500, message: e.message || "Couldn't load this studio." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // Push the studio's chosen theme (preset + per-key overrides) into shadcn CSS
  // variables on the document root so every Tailwind utility we use below
  // (bg-background, text-foreground, bg-primary, text-muted-foreground, etc.)
  // automatically renders in the tenant's brand colors. The cleanup function
  // restores the previous values when the user navigates away from /s/{slug}.
  useEffect(() => {
    if (!site) return undefined;
    const cleanup = applyTheme({
      themeId: site.theme_id || "editorial",
      overrides: site.theme_overrides || {},
    });
    return cleanup;
  }, [site]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="studio-public-error">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-4" />
          <h1 className="text-3xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            {error.code === 410 ? "Studio temporarily unavailable" : "Studio not found"}
          </h1>
          <p className="text-muted-foreground text-sm mb-6">{error.message}</p>
          <Link to="/lumetier" className="text-xs uppercase tracking-[0.22em] underline">
            Lumetier ←
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="studio-public">
      <StudioHeader studio={studio} site={site} />
      <Hero studio={studio} site={site} />
      <About site={site} />
      <Portfolio site={site} />
      <ServicesAndBooking
        slug={slug}
        studio={studio}
        services={services}
        availability={availability}
      />
      <ContactBlock slug={slug} site={site} />
      <Footer studio={studio} />
    </div>
  );
}

function StudioHeader({ studio, site }) {
  const displayName = site?.studio_name || studio.name;
  return (
    <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20" data-testid="studio-header">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="text-lg tracking-[0.32em] font-light"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {displayName.toUpperCase()}
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-6 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <a href="#about" className="hover:text-foreground">About</a>
          <a href="#portfolio" className="hover:text-foreground">Portfolio</a>
          <a href="#services" className="hover:text-foreground">Sessions</a>
          <a href="#contact" className="hover:text-foreground">Contact</a>
          <a href="#services" className="px-4 py-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="header-book-cta">
            Book
          </a>
        </div>
      </div>
    </header>
  );
}

function Hero({ studio, site }) {
  // Hero text falls back gracefully if owner hasn't customized site_settings
  const eyebrow = site?.hero_eyebrow || `Powered by Lumetier · ${studio.plan}`;
  const line1 = site?.hero_title_line1 || "Crafted with care.";
  const line2 = site?.hero_title_line2 || "Captured with intent.";
  const description = site?.hero_description || `Welcome to ${studio.name}. Where bookings, galleries, and your story all live in one place.`;
  const heroImage = site?.hero_image && site.hero_image !== "/hero.jpg" ? site.hero_image : null;

  return (
    <section className="relative" data-testid="studio-hero">
      <div className="max-w-6xl mx-auto px-6 py-20 md:py-32">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-4 inline-flex items-center gap-1.5" style={{ color: "hsl(var(--accent))" }}>
              <Sparkles className="w-3 h-3" /> {eyebrow}
            </div>
            <h1 className="text-5xl md:text-6xl font-light leading-[1.05] mb-6" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              {line1}<br/>
              <span className="italic" style={{ color: "hsl(var(--accent))" }}>{line2}</span>
            </h1>
            <p className="text-muted-foreground max-w-md mb-8 leading-relaxed">{description}</p>
            <a
              href="#services"
              data-testid="hero-book-cta"
              className="inline-flex items-center gap-2 h-12 px-7 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:opacity-90"
            >
              Book a session <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="aspect-[4/5] bg-muted relative overflow-hidden">
            {heroImage ? (
              <img src={heroImage} alt={studio.name} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-xs uppercase tracking-[0.22em]">
                Your hero image
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function About({ site }) {
  return (
    <section id="about" className="bg-muted py-20 md:py-28" data-testid="studio-about">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <div className="text-[11px] uppercase tracking-[0.32em] mb-4" style={{ color: "hsl(var(--accent))" }}>
          {site?.about_eyebrow || "The Studio"}
        </div>
        <h2 className="text-3xl md:text-4xl font-light mb-6 leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          {site?.about_title || "A modern studio for every chapter of your story."}
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          {site?.about_body || "Bookings, contracts, galleries, and reviews — all in one place. Built for photographers who'd rather spend their time behind the camera than buried in spreadsheets."}
        </p>
      </div>
    </section>
  );
}

function Portfolio({ site }) {
  /* Renders the studio's portfolio images as a responsive grid. Returns null when the
     owner hasn't uploaded any photos yet — better to omit the section entirely than
     leave an awkward empty grid that dilutes the page.
     Tapping a tile opens a full-screen lightbox with prev/next + ESC-to-close. */
  const images = (site?.portfolio_images || []).filter(Boolean);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Keyboard navigation only mounts when lightbox is open. Body-scroll lock too.
  useEffect(() => {
    if (lightboxIdx === null) return;
    function onKey(e) {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i === null ? null : (i - 1 + images.length) % images.length));
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i === null ? null : (i + 1) % images.length));
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, images.length]);

  if (!images.length) return null;

  return (
    <section id="portfolio" className="py-20 md:py-28" data-testid="studio-portfolio">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-[0.32em] mb-3" style={{ color: "hsl(var(--accent))" }}>
            {site?.portfolio_eyebrow || "Selected Work"}
          </div>
          <h2 className="text-4xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            {site?.portfolio_title || "Recent sessions"}
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="portfolio-grid">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setLightboxIdx(i)}
              data-testid={`portfolio-tile-${i}`}
              className={`group relative overflow-hidden bg-muted ${
                // Vary aspect ratio in a deterministic pattern for a gallery-wall feel
                // without needing per-image metadata. Every 4th tile is a tall portrait.
                i % 4 === 0 ? "aspect-[3/4] row-span-2" : "aspect-square"
              }`}
            >
              <img
                src={src}
                alt={`Portfolio ${i + 1}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox — only mounted when a tile is clicked */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-foreground/95 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
          data-testid="portfolio-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-background hover:text-background/80"
            aria-label="Close"
            data-testid="portfolio-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i - 1 + images.length) % images.length); }}
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-background hover:text-background/80 bg-foreground/40 hover:bg-foreground/70"
                aria-label="Previous"
                data-testid="portfolio-lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i + 1) % images.length); }}
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-background hover:text-background/80 bg-foreground/40 hover:bg-foreground/70"
                aria-label="Next"
                data-testid="portfolio-lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img
            src={images[lightboxIdx]}
            alt={`Portfolio ${lightboxIdx + 1}`}
            className="max-w-[92vw] max-h-[88vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            data-testid="portfolio-lightbox-image"
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-background uppercase tracking-[0.22em]">
            {lightboxIdx + 1} / {images.length}
          </div>
        </div>
      )}
    </section>
  );
}



function ServicesAndBooking({ slug, studio, services, availability }) {
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);

  if (!services.length) {
    return (
      <section id="services" className="py-20 md:py-28" data-testid="studio-services-empty">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-[11px] uppercase tracking-[0.32em] mb-4" style={{ color: "hsl(var(--accent))" }}>Sessions</div>
          <h2 className="text-3xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Sessions coming soon
          </h2>
          <p className="text-muted-foreground text-sm">
            {studio.name} is setting up their session offerings. Reach out via the contact form below and we&apos;ll let you know the moment booking opens.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 md:py-28" data-testid="studio-services">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-[0.32em] mb-3" style={{ color: "hsl(var(--accent))" }}>Sessions</div>
          <h2 className="text-4xl font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Pick a session to book
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((svc) => (
            <button
              key={svc.id}
              onClick={() => { setSelectedService(svc); setSelectedTier(svc.tiers?.[0] || null); }}
              data-testid={`service-card-${svc.slug}`}
              className={`text-left bg-card border-2 transition-colors p-6 ${selectedService?.id === svc.id ? "border-primary" : "border-border hover:border-foreground/40"}`}
            >
              <div className="font-medium text-lg mb-2">{svc.name}</div>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{svc.description}</p>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                From ${Math.min(...(svc.tiers || []).map((t) => Number(t.price) || 0))}
              </div>
            </button>
          ))}
        </div>

        {selectedService && (
          <BookingFlow
            key={selectedService.id}
            slug={slug}
            service={selectedService}
            selectedTier={selectedTier}
            setSelectedTier={setSelectedTier}
            availability={availability}
          />
        )}
      </div>
    </section>
  );
}

function BookingFlow({ slug, service, selectedTier, setSelectedTier, availability }) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", policy: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {reference, deposit_amount, tier_price}

  const isoDate = useMemo(() => date ? date.toISOString().slice(0, 10) : "", [date]);
  const blockedDates = useMemo(() => new Set(availability.blocked_dates || []), [availability]);
  const slotsForDate = useMemo(() => availability.blocked_slots?.[isoDate] || [], [availability, isoDate]);

  const deposit = useMemo(() => {
    if (!selectedTier) return 0;
    const pct = Number(service.deposit_percent) || 0;
    return Math.round(Number(selectedTier.price) * pct) / 100;
  }, [service.deposit_percent, selectedTier]);

  function isDateDisabled(d) {
    // Block past dates + every full-day block
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    return blockedDates.has(d.toISOString().slice(0, 10));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedTier) return toast.error("Pick a tier first.");
    if (!date) return toast.error("Pick a date.");
    if (!time) return toast.error("Pick a time.");
    if (!form.name || !form.email) return toast.error("Name and email are required.");
    if (!form.policy) return toast.error("Please accept the studio policy to book.");
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/s/${slug}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: service.id,
          tier_name: selectedTier.name,
          session_date: isoDate,
          session_time: time,
          customer_name: form.name.trim(),
          customer_email: form.email.trim(),
          customer_phone: form.phone.trim(),
          notes: form.notes.trim(),
          policy_accepted: true,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Couldn't submit booking.");
      setSuccess({ reference: data.reference, deposit_amount: data.deposit_amount, tier_price: data.tier_price });
      toast.success(`Booking received · ${data.reference}`);
    } catch (err) {
      toast.error(err.message || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 px-8 py-10 max-w-2xl mx-auto text-center" data-testid="booking-success">
        <Check className="w-10 h-10 text-emerald-700 mx-auto mb-4" />
        <h3 className="text-3xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#064E3B" }}>
          You&apos;re booked.
        </h3>
        <p className="text-stone-700 mb-2">
          Reference: <strong className="font-mono">{success.reference}</strong>
        </p>
        <p className="text-stone-700 text-sm">
          Total ${success.tier_price?.toFixed(2)} · Deposit ${success.deposit_amount?.toFixed(2)}
        </p>
        <p className="text-stone-500 text-xs mt-4">
          A confirmation has been emailed to {form.email}. The studio will be in touch to finalize details.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border p-8 max-w-4xl mx-auto" data-testid="booking-flow">
      <div className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-1">Step 1</div>
      <h3 className="text-xl font-medium mb-4">Choose a tier</h3>
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        {(service.tiers || []).map((t) => (
          <button
            key={t.name}
            onClick={() => setSelectedTier(t)}
            data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
            className={`text-left border-2 p-4 transition-colors ${selectedTier?.name === t.name ? "border-primary bg-muted" : "border-border hover:border-foreground/40"}`}
          >
            <div className="font-medium mb-1">{t.name}</div>
            <div className="text-2xl font-light mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              ${t.price}
            </div>
            {t.description && <div className="text-xs text-muted-foreground line-clamp-2">{t.description}</div>}
          </button>
        ))}
      </div>

      <div className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-1">Step 2</div>
      <h3 className="text-xl font-medium mb-4">Pick a date & time</h3>
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="border border-border p-3">
          <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setTime(""); }} disabled={isDateDisabled} initialFocus />
        </div>
        <div>
          {date ? (
            <>
              <div className="text-sm text-foreground mb-3 inline-flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" /> {fmtDate(date)}
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                {DEFAULT_SLOTS.map((s) => {
                  const disabled = slotsForDate.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={disabled}
                      onClick={() => setTime(s)}
                      data-testid={`time-slot-${s.replace(":", "")}`}
                      className={`py-2 px-3 text-sm border transition-colors ${time === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground/60"} disabled:opacity-30 disabled:cursor-not-allowed`}
                    >
                      <Clock className="w-3 h-3 inline mr-1" />{s}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Select a date to see available time slots.</div>
          )}
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-1">Step 3</div>
      <h3 className="text-xl font-medium mb-4">Your details</h3>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
        <div className="grid md:grid-cols-2 gap-4">
          <input type="text" required placeholder="Full name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="booking-name" className="bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
          <input type="email" required placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="booking-email" className="bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
        </div>
        <input type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="booking-phone" className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
        <textarea placeholder="Anything you'd like the studio to know?" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3} data-testid="booking-notes" className="w-full bg-background border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" required checked={form.policy} onChange={(e) => setForm((f) => ({ ...f, policy: e.target.checked }))} data-testid="booking-policy" className="mt-1" />
          <span>I&apos;ve read and accept the studio&apos;s booking policy & deposit terms.</span>
        </label>

        {selectedTier && (
          <div className="bg-muted border border-border px-4 py-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><strong>${selectedTier.price}</strong></div>
            <div className="flex justify-between text-muted-foreground"><span>Deposit ({service.deposit_percent || 0}%)</span><span>${deposit.toFixed(2)}</span></div>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="booking-submit"
          className="w-full h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Confirm booking <ChevronRight className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}

function ContactBlock({ slug, site }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Name, email, and message are required.");
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/s/${slug}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!resp.ok) throw new Error("Couldn't send message.");
      setSent(true);
      toast.success("Message sent — you'll hear back soon.");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="bg-muted py-20 md:py-24" data-testid="studio-contact">
      <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] mb-3" style={{ color: "hsl(var(--accent))" }}>Get in touch</div>
          <h2 className="text-4xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Questions before booking?
          </h2>
          <p className="text-muted-foreground text-sm mb-8">
            Drop us a note and we&apos;ll get back to you within 24 hours.
          </p>
          <div className="space-y-3 text-sm text-foreground">
            {site?.contact_email && (
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" /> {site.contact_email}</div>
            )}
            {site?.contact_phone && (
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> {site.contact_phone}</div>
            )}
            {site?.contact_location && (
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /> {site.contact_location}</div>
            )}
          </div>
        </div>

        {sent ? (
          <div className="bg-emerald-50 border border-emerald-200 px-6 py-8 text-center" data-testid="contact-success">
            <Check className="w-8 h-8 text-emerald-700 mx-auto mb-3" />
            <div className="font-medium mb-1" style={{ color: "#064E3B" }}>Message sent.</div>
            <div className="text-sm text-stone-600">We&apos;ll be in touch soon.</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
            <input type="text" required placeholder="Name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="contact-name" className="w-full border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
            <input type="email" required placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="contact-email" className="w-full border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
            <input type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="contact-phone" className="w-full border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
            <textarea required placeholder="Your message *" rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="contact-message" className="w-full border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus:border-foreground" />
            <button type="submit" disabled={submitting} data-testid="contact-submit" className="w-full h-11 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send message"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer({ studio }) {
  return (
    <footer className="py-10 border-t border-border bg-background">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
        <div>© {new Date().getFullYear()} {studio.name}. All rights reserved.</div>
        <Link to="/lumetier" className="uppercase tracking-[0.22em] hover:text-foreground">
          Powered by Lumetier
        </Link>
      </div>
    </footer>
  );
}
